/**
 * Builder editor context + hook + types. Kept in a component-free module so the
 * provider file can export only its component (fast-refresh friendly).
 */
import { createContext, useContext } from 'react'
import type { BuilderRecord } from '../../api/client'
import type {
  SiteSchema,
  Section,
  DesignSystem,
  BorderRadiusScale,
  SpacingScale,
  SupportedFont,
  SectionKind,
  LayoutId,
} from '../../site-builder/schema'

/** A trimmed string or undefined (empty/whitespace → undefined). */
function trimmed(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v : undefined
}

/**
 * A google-maps embed URL from a free-text address (mirrors the Contact component and
 * the editor's setBusinessField/setLocationField). Shared here so both the editor and
 * the reload path (applyBusinessInfoToContent) compute the SAME embed URL.
 */
export function mapEmbedUrl(address: string): string | undefined {
  return address.trim() ? `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed` : undefined
}

/** Resolve an asset id to its stored URL (data: or http(s):), or undefined if unset. */
export function assetUrl(schema: SiteSchema, assetId?: string): string | undefined {
  if (!assetId) return undefined
  return schema.assets.items.find((a) => a.id === assetId)?.url
}

/** Max image file size accepted for uploads (base64 stays under the backend cap). */
export const MAX_IMAGE_BYTES = 450_000

/**
 * Validate + read an image file as a data URL. Shared by every image-picking control
 * (`ImageUploadField` and the Gallery editor's thumbnail strip) so the size/type rules and
 * error copy stay identical everywhere in the Manual Editor.
 */
export function readImageFile(file: File): Promise<string> {
  return readImageFileWithLimit(file, MAX_IMAGE_BYTES, `Image is too large (max ${Math.round(MAX_IMAGE_BYTES / 1000)}KB).`)
}

/** Gallery-only cap (Gallery UX improvement): a larger per-image limit than the rest of the editor. */
export const MAX_GALLERY_IMAGE_BYTES = 2_000_000

/** Gallery-only cap: the gallery never holds more than this many images. */
export const MAX_GALLERY_IMAGES = 10

/** Same validation as `readImageFile`, but against the Gallery's own (larger) size cap. */
export function readGalleryImageFile(file: File): Promise<string> {
  return readImageFileWithLimit(
    file,
    MAX_GALLERY_IMAGE_BYTES,
    `Image is too large (max ${Math.round(MAX_GALLERY_IMAGE_BYTES / 1_000_000)}MB).`,
  )
}

function readImageFileWithLimit(file: File, maxBytes: number, tooLargeMessage: string): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Please choose an image file.'))
      return
    }
    if (file.size > maxBytes) {
      reject(new Error(tooLargeMessage))
      return
    }
    const reader = new FileReader()
    reader.onload = () => (typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('Could not read the file.')))
    reader.onerror = () => reject(new Error('Could not read the file.'))
    reader.readAsDataURL(file)
  })
}

/**
 * Mirror persisted `businessInfo` onto the rendered section content so a reloaded/saved
 * site reflects the user's Settings edits (the backend persists businessInfo but NOT
 * section content, so on reload the stored content.social/contact is stale). This
 * reproduces exactly what the editor's setSocial/setBusinessField do live.
 *
 * No `businessInfo` (e.g. a freshly generated site loaded standalone) → no-op, so the
 * generated content.social renders. Only overwrites fields businessInfo actually has
 * (empty businessInfo fields are skipped, never clobbering real content). Returns a NEW
 * schema only when something changed.
 */
export function applyBusinessInfoToContent(schema: SiteSchema): SiteSchema {
  const bi = schema.businessInfo
  if (!bi) return schema

  const urls = Object.values(bi.social ?? {}).filter(
    (u): u is string => typeof u === 'string' && u.trim().length > 0,
  )

  let changed = false
  let footerDone = false
  let contactDone = false

  const map = (s: Section): Section => {
    if (!footerDone && s.kind === 'footer') {
      footerDone = true
      changed = true
      // Mirror social AND the business contact/location onto the footer so its Contact
      // column has data (the generator only populates the contact section). Derived from
      // the SAME businessInfo the contact section uses, so both stay in sync.
      const content = { ...s.content, social: urls }
      content.contact = {
        phone: trimmed(bi.phone) ?? content.contact?.phone,
        email: trimmed(bi.email) ?? content.contact?.email,
        website: content.contact?.website,
      }
      const address = trimmed(bi.street)
      if (address !== undefined) {
        content.location = { ...content.location, address, mapUrl: mapEmbedUrl(address) }
      }
      return { ...s, content }
    }
    if (!contactDone && s.kind === 'contact') {
      contactDone = true
      changed = true
      const content = { ...s.content, social: urls }
      content.contact = {
        phone: trimmed(bi.phone) ?? content.contact?.phone,
        email: trimmed(bi.email) ?? content.contact?.email,
        website: content.contact?.website,
      }
      const address = trimmed(bi.street)
      if (address !== undefined) {
        content.location = { ...content.location, address, mapUrl: mapEmbedUrl(address) }
      }
      return { ...s, content }
    }
    return s
  }

  const pages = schema.pages.map((page) => ({ ...page, sections: page.sections.map(map) }))
  return changed ? { ...schema, pages } : schema
}

/**
 * Human labels for section kinds, shared by the Sections UI. UI/UX terminology only — the
 * underlying SiteSchema kind stays `"hero"`/`"services"`/`"about"` (registry key, runtime,
 * AI generator, backend); only what the Manual Editor's user sees is renamed ("Banner",
 * "Features", "Content").
 */
export const SECTION_KIND_LABEL: Record<SectionKind, string> = {
  navbar: 'Navigation',
  hero: 'Banner',
  about: 'Content',
  services: 'Features',
  gallery: 'Gallery',
  reviews: 'Reviews',
  faq: 'FAQ',
  contact: 'Locations',
  footer: 'Footer',
}

/** 'saved' = clean & persisted · 'dirty' = unsaved edits · 'saving' · 'error'. */
export type SaveState = 'saved' | 'dirty' | 'saving' | 'error'

export type BusinessTextField = 'email' | 'phone' | 'street' | 'city' | 'state' | 'zip' | 'unit'
export type SocialPlatform = 'facebook' | 'instagram' | 'x' | 'linkedin' | 'tiktok' | 'youtube'

/** Shared social platform field registry — used by the Settings › Social links screen and
 *  the Footer editor's Social links fieldset (both write through `setSocial`). */
export const SOCIAL_PLATFORM_FIELDS: { key: SocialPlatform; label: string; placeholder: string }[] = [
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/…' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/…' },
  { key: 'x', label: 'X (Twitter)', placeholder: 'https://x.com/…' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/…' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@…' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@…' },
]

/** Editable section heading-group text fields. */
export type SectionTextField = 'eyebrow' | 'heading' | 'subheading'
/** Editable text fields on a repeated content item. */
export type ItemTextField = 'title' | 'description' | 'value'
/** Editable text fields on an action (button). */
export type ActionTextField = 'label' | 'value'
/** Editable text fields on a navbar/footer link item. */
export type NavLinkField = 'title' | 'value'
/** Editable contact detail fields (→ content.contact). */
export type ContactField = 'phone' | 'email' | 'website'

export interface BuilderEditorValue {
  readonly builder: BuilderRecord
  readonly schema: SiteSchema
  readonly saveState: SaveState
  readonly lastSavedAt: string
  /** True when there are unsaved edits (or the last save failed). */
  readonly dirty: boolean

  /**
   * CSS custom properties (name → value) for ONLY the theme fields the user has edited
   * this session. Streamed to the preview so it overrides the reference default per-field;
   * empty by default (preview stays pixel-perfect reference). See Phase 11.6 Part A.
   */
  readonly themeOverrides: Record<string, string>

  // ── Design (RuntimeTheme + branding) ─────────────────────────────────────────
  setDesignSystem(v: DesignSystem): void
  /** Drives the site's visual chrome (navbar/footer/buttons/links). AI-generated; independent of brandColor. */
  setPrimaryColor(v: string): void
  /** Business-identity color, default #000000. Independent of primaryColor. */
  setBrandColor(v: string): void
  setHeadingFont(v: SupportedFont): void
  setBodyFont(v: SupportedFont): void
  setBorderRadius(v: BorderRadiusScale): void
  setSpacing(v: SpacingScale): void
  /** Set (data/http URL) or clear (null) the logo / favicon. */
  setLogo(src: string | null): void
  setFavicon(src: string | null): void
  setLogoSize(px: number): void

  // ── Settings (builder metadata) ──────────────────────────────────────────────
  setSeoTitle(v: string): void
  setSeoDescription(v: string): void
  setBusinessField(field: BusinessTextField, value: string): void
  setSocial(platform: SocialPlatform, value: string): void

  // ── Sections (live working schema — pages[0].sections; not persisted this phase) ──
  /** Set a section's layout (must be one of registry.supportedLayouts for its kind). */
  setSectionLayout(sectionId: string, layout: LayoutId): void
  /** Flip a section's visibility. */
  toggleSectionVisible(sectionId: string): void
  /**
   * Apply a full drag-and-drop reorder of the MOVABLE sections (everything except the
   * anchored navbar/footer, which the caller never includes). `movableIds` must be a
   * permutation of the current movable section ids; any other input is a no-op — same
   * anchoring guarantee the old up/down `moveSection` had (navbar first, footer last).
   */
  reorderSections(movableIds: string[]): void
  /** Edit a section's heading-group text (eyebrow/heading/subheading). */
  setSectionText(sectionId: string, field: SectionTextField, value: string): void
  /** Edit a repeated content item's text field. */
  setItemField(sectionId: string, itemId: string, field: ItemTextField, value: string): void
  /** Edit a review item's star rating. */
  setItemRating(sectionId: string, itemId: string, value: number): void
  /** Append a new content item (stable generated id). */
  addItem(sectionId: string): void
  /** Remove a content item. */
  removeItem(sectionId: string, itemId: string): void
  /** Reorder a content item up/down within its section's items list. */
  moveItem(sectionId: string, itemId: string, dir: 'up' | 'down'): void
  /** Set (data/http URL) or clear (null) a content item's icon/image, through the asset library. */
  setItemImage(sectionId: string, itemId: string, src: string | null): void
  /** Edit an action (button) label/value. */
  setActionField(sectionId: string, actionId: string, field: ActionTextField, value: string): void
  /** Edit a navbar/footer link item's title/value. */
  setNavLink(sectionId: string, itemId: string, field: NavLinkField, value: string): void
  /** Edit a contact section's contact details (→ content.contact). */
  setContactField(sectionId: string, field: ContactField, value: string): void
  /** Edit a contact section's address (→ content.location, recomputes a maps embed URL). */
  setLocationField(sectionId: string, field: 'address', value: string): void
  /** Set (data/http URL) or clear (null) a section's single background-media slot, through the asset library. */
  setSectionImage(sectionId: string, src: string | null): void
  /** Append a new gallery image (a new asset + a MediaItem pointing at it). */
  addGalleryImage(sectionId: string, src: string): void
  /** Remove a gallery image by its MediaItem id (prunes the backing asset unless still referenced elsewhere). */
  removeGalleryImage(sectionId: string, mediaId: string): void
  /** Replace a gallery image's bytes in place (same MediaItem/asset id, new asset URL). */
  replaceGalleryImage(sectionId: string, mediaId: string, src: string): void
  /** Reorder a gallery image up/down within its section's media list. */
  moveGalleryImage(sectionId: string, mediaId: string, dir: 'up' | 'down'): void

  // ── Live Preview ↔ sidebar sync (Section Navigation UX) ──────────────────────
  /**
   * The id of the section currently most visible in the Live Preview iframe, kept in sync
   * by the preview's own IntersectionObserver (see `site-builder/runtime/previewSync.ts`,
   * wired up in LivePreviewPage.tsx). `null` until the preview has reported once, or when
   * it's not embedded. Lets the sidebar's section list keep tracking scroll position even
   * when the user hasn't clicked anything.
   */
  readonly previewActiveSectionId: string | null
  /** Called by BuilderWorkspace's message listener when the preview reports a new active section. */
  setPreviewActiveSectionId(sectionId: string | null): void
  /**
   * Ask the Live Preview to smooth-scroll to a section. A plain state signal (not a direct
   * iframe call) so the sidebar never needs to know the iframe exists — BuilderWorkspace
   * (which owns the iframe) watches this value and posts the actual scroll message.
   */
  readonly scrollRequest: { sectionId: string; nonce: number } | null
  requestScrollToSection(sectionId: string): void

  /** Persist all pending edits to the backend (explicit — there is no autosave). */
  save(): void
}

export const BuilderEditorCtx = createContext<BuilderEditorValue | null>(null)

export function useBuilderEditor(): BuilderEditorValue {
  const v = useContext(BuilderEditorCtx)
  if (!v) throw new Error('useBuilderEditor must be used within a BuilderEditorProvider')
  return v
}
